import { Box, BoxProps, ColorOptions, Icon, Spacer, Text, TextProps } from '@jfs/ui-new'
import * as React from 'react'
import { FC, useCallback, useEffect, useState } from 'react'
import { useLazyApiData, useStores } from '../../data/store'
import { MobileProcessing } from '../../screens/PaymentProcesseing/MobileProcessing'
import { addTwoDecimalZeroes, analyticsEvent, convertHexToRGBA, formatAmount, isIFrame } from '../../utils'
import { PAYMENT_MODE_SUBTYPES, TEMPLATE, THEME_PADDING, windowConfig } from '../../utils/constants'
import { staticStrings } from '../../utils/string'
import { CustomFlatList } from '../CustomFlatList'
import { PaySecurelyButton } from '../PaySecurelyButton'
import { ConvenienceFeeModal } from './ConvenienceFeeModal'
import { listView } from './listView'
import { listViewWithoutPopularBanks } from './listViewWithoutPopularBanks'
import { listViewWithoutSearch } from './listViewWithoutSearch'
import { LogoImage } from './LogoImage'
import { toneListView } from './toneListView'
const getImage = (item: any, type: any) => {
  return <LogoImage imgUrl={item?.imageUrl ?? ''} type={type} />
}

const ParentContainer = ({ children }: any) => {
  return <Box flex={1}>{children}</Box>
}

const PayBtn = ({ disabled = false, amount, onClickPaySecure, isLoading }: any) => {
  return (
    <Box justifyContent="flex-end" paddingTop={8}>
      <PaySecurelyButton
        testID={'paySecurelyBtn'}
        amount={amount}
        onPress={onClickPaySecure}
        disabled={disabled}
        isLoading={isLoading}
      />
    </Box>
  )
}

const SectionList: FC<{ type: number; btnFooter?: React.ReactNode; hidePopulerBankList?: boolean }> = ({
  type,
  btnFooter,
  hidePopulerBankList = false,
}: {
  type: number
  btnFooter?: React.ReactNode
}) => {
  const {
    getInstrumentsList,
    setSelectedPayment,
    getTransactionAmount,
    theme,
    validateOffer,
    applyOffer,
    offerApplied,
    updateIsOfferApplied,
    isMerchantBanksAvailable,
    template,
    getConvenienceFee,
    uniqueWindowId,
  } = useStores((root) => ({
    getInstrumentsList: root.getInstrumentsList,
    getTransactionAmount: root.getTransactionAmount,
    setSelectedPayment: root.setSelectedPayment,
    theme: root?.config?.merchantTheme,
    uniqueWindowId: root.config.uniqueWindowId,
    validateOffer: root.offerValidateApi,
    applyOffer: root.offerTxnApplyApi,
    offerApplied: root.isOfferApplied,
    updateIsOfferApplied: root.transaction.updateIsOfferApplied,
    getOfferBankIds: root.getOfferBankIds,
    isMerchantBanksAvailable: root.isMerchantBanksAvailable,
    template: root.merchant.template,
    getConvenienceFee: root.transaction.getConvenienceFee,
  }))

  const [validateOfferApi, { state: validateOfferState }] = useLazyApiData(validateOffer)
  const [lazyApplyOffer, { state: applyOfferState }] = useLazyApiData(applyOffer)

  const { REACT_APP_URL = '', REACT_APP_ENDPOINT = '' } = process.env

  const [showModal, setShowModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState('')
  const [convenienceFee, setConvenienceFee] = useState<any>({})
  const [showPaymentProcessModal, setShowPaymentProcessModal] = useState(false)
  const [isCorpBankSelected, setIsCorpBankSelected] = useState(false)

  const isOfferApplied = offerApplied(isCorpBankSelected ? PAYMENT_MODE_SUBTYPES.CORP_NB : type)
  useEffect(() => {
    if (selectedItem !== '') {
      isOfferApplied &&
        validateOfferApi(
          { bankId: selectedItem },
          {
            type: 110,
            subType: isCorpBankSelected ? PAYMENT_MODE_SUBTYPES.CORP_NB : type,
          },
        )
    }
  }, [isCorpBankSelected, isOfferApplied, selectedItem, type, validateOfferApi])

  const style = styles(theme, THEME_PADDING)
  const renderFLItem = ({ item }: any) => {
    const isSelected = selectedItem === `${item?.id}`

    const itemClick = () => {
      setSelectedItem(`${item.id}`)
      setIsCorpBankSelected(item.isCnb)
      const lConvenienceFee: any = getConvenienceFee(
        item.isCnb ? PAYMENT_MODE_SUBTYPES.CORP_NB : type,
        isOfferApplied,
        item.id,
      )
      if (lConvenienceFee?.totalFee) {
        setConvenienceFee(lConvenienceFee)
      } else {
        setConvenienceFee('')
      }
    }

    return (
      <Box flexDirection="column" height={64}>
        <Box
          f={1}
          paddingVertical={8}
          flexDirection="row"
          alignItems="center"
          onPress={itemClick}
          testID={`bankId-${item?.id}`}
          underlayColor={convertHexToRGBA(theme?.brandColor, 10) as ColorOptions}>
          <Box {...style.logoContainer}>{getImage(item, type)}</Box>
          <Spacer direction="horizontal" size={16} />
          <Box flex={1}>
            <Text
              {...style.itemText}
              textAlign="left"
              numberOfLines={2}
              color={isSelected ? (theme?.brandColor as ColorOptions) : (theme?.bodyTextColor as ColorOptions)}>
              {item?.text ?? ''}
            </Text>
            {isSelected && convenienceFeeComp()}
          </Box>
          {isSelected && (
            <Box paddingRight={16}>
              <Icon id="ROUND_TICK" color={theme?.brandColor as ColorOptions} size={16} />
            </Box>
          )}
        </Box>
        <Box {...style.separatorStyle} />
      </Box>
    )
  }

  const instruments = getInstrumentsList(type)
  const otherKey =
    type === PAYMENT_MODE_SUBTYPES.NB || type === PAYMENT_MODE_SUBTYPES.CORP_NB ? 'Other banks' : 'Other Wallets' // @:TODO htag
  const otherBanks = instruments?.find((item: any) => item.title === otherKey) ?? []

  const loadProcessingScreen = useCallback(() => {
    const loaderUrl = REACT_APP_ENDPOINT
      ? `${REACT_APP_URL}/${REACT_APP_ENDPOINT}/loader.html`
      : `${REACT_APP_URL}/loader.html`
    if (!isIFrame()) {
      window.currentChild = window
    } else {
      window.currentChild = window.open(loaderUrl, `jiopayWindow-${uniqueWindowId}`, windowConfig)
      window.currentChild?.focus()
    }
    setShowPaymentProcessModal(true)
  }, [REACT_APP_ENDPOINT, REACT_APP_URL, uniqueWindowId])

  useEffect(() => {
    if (applyOfferState === 'fulfilled') {
      loadProcessingScreen()
    }
  }, [applyOfferState, loadProcessingScreen])

  const onClickPaySecure = () => {
    setSelectedPayment(selectedItem, isCorpBankSelected ? PAYMENT_MODE_SUBTYPES.CORP_NB : type)
    analyticsEvent('pay_button_pressed', {
      mode:
        type === PAYMENT_MODE_SUBTYPES.NB
          ? 'netbanking'
          : type === PAYMENT_MODE_SUBTYPES.CORP_NB
            ? 'corporateNetbanking'
            : 'wallet',
      amount: getPayableAmount(),
    })
    if (isOfferApplied && applyOfferState !== 'rejected' && validateOfferState === 'fulfilled') {
      lazyApplyOffer()
    } else {
      updateIsOfferApplied(false)
      loadProcessingScreen()
    }
  }

  const listViewObj: any = {
    [TEMPLATE.MILK_BASKET]: toneListView,
    default: listView,
  }

  const listContainerStyle: any = {
    [TEMPLATE.MILK_BASKET]: style.flatListToneContainer,
    default: style.flatListContainer,
  }

  const footerContainerStyle: any = {
    [TEMPLATE.MILK_BASKET]: style.tOneFooterContainer,
    default: style.footerContainer,
  }

  const listViewComp = listViewObj[template?.id] ?? listViewObj.default
  const containerStyle = listContainerStyle[template?.id] ?? listContainerStyle.default
  const footerStyle = footerContainerStyle[template?.id] ?? footerContainerStyle.default
  const convenienceFeeComp = () => {
    if (convenienceFee?.totalFee && isOfferApplied && validateOfferState === 'fulfilled') {
      return (
        <Box flexDirection="row">
          <Text {...style.offerAvailable}>{staticStrings.offerApplied}</Text>
          <Text {...style.convenienceFeeText}>{`+ ₹${formatAmount(convenienceFee?.totalFee)} Conv fee added.`}</Text>
          <Spacer direction="horizontal" size={4} />
          <Box
            underlayColor={'TRANSPARENT'}
            onPress={() => {
              setShowModal(true)
            }}>
            <Icon color={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions} id="INFO" size={12} />
          </Box>
        </Box>
      )
    }

    if (convenienceFee?.totalFee && isOfferApplied && validateOfferState === 'rejected') {
      return (
        <Box flexDirection="row">
          <Text {...style.offerNotAvailable}>Offer not available</Text>
          <Text {...style.convenienceFeeText}>{`+ ₹${formatAmount(convenienceFee?.totalFee)} Conv fee added.`}</Text>
          <Spacer direction="horizontal" size={4} />
          <Box
            underlayColor={'TRANSPARENT'}
            onPress={() => {
              setShowModal(true)
            }}>
            <Icon color={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions} id="INFO" size={12} />
          </Box>
        </Box>
      )
    }

    if (convenienceFee?.totalFee && !isOfferApplied) {
      return (
        <Box flexDirection="row">
          <Text {...style.convenienceFeeText}>{`Convenience fee ₹${formatAmount(
            convenienceFee?.totalFee,
          )} applicable.`}</Text>
          <Spacer direction="horizontal" size={4} />
          <Box
            underlayColor={'TRANSPARENT'}
            onPress={() => {
              setShowModal(true)
            }}>
            <Icon color={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions} id="INFO" size={12} />
          </Box>
        </Box>
      )
    }

    if (isOfferApplied && validateOfferState === 'fulfilled') {
      return <Text {...style.offerAvailable}>{staticStrings.offerApplied}</Text>
    }

    if (isOfferApplied && validateOfferState === 'rejected') {
      return (
        <Text {...style.offerNotAvailable}>
          {type === PAYMENT_MODE_SUBTYPES.NB
            ? `${staticStrings.invalidOffer} bank`
            : `${staticStrings.invalidOffer} wallet`}
        </Text>
      )
    }

    return null
  }

  const getPayableAmount = () => {
    return (
      Number(getTransactionAmount()) +
      Number(convenienceFee?.totalFee ?? 0) +
      Number(convenienceFee?.sgst ?? 0) +
      Number(convenienceFee?.cgst ?? 0) +
      Number(convenienceFee?.igst ?? 0) +
      Number(convenienceFee?.ugst ?? 0) +
      Number(convenienceFee?.tds ?? 0) +
      Number(convenienceFee?.tcs ?? 0)
    )
  }

  const setSelectItem = (item: any) => {
    setSelectedItem(item)
    const lConvenienceFee: any = getConvenienceFee(
      isCorpBankSelected ? PAYMENT_MODE_SUBTYPES.CORP_NB : type,
      isOfferApplied,
      item,
    )
    if (lConvenienceFee?.totalFee) {
      setConvenienceFee(lConvenienceFee)
    } else {
      setConvenienceFee('')
    }
  }

  console.log('convenienceFeeComp', convenienceFee)

  return (
    <Box flex={1}>
      <ParentContainer>
        <Box {...containerStyle} testID="flatlistWrapper">
          <CustomFlatList
            data={otherBanks?.data ?? []}
            renderItem={renderFLItem}
            renderScrollView={
              isMerchantBanksAvailable(type)
                ? listViewWithoutSearch
                : hidePopulerBankList
                  ? listViewWithoutPopularBanks
                  : listViewComp
            }
            type={type}
            setShowPaymentProcessModal={setShowPaymentProcessModal}
            selectedItem={selectedItem}
            setSelectedItem={setSelectItem}
            showofferInfo={false}
            validateOfferState={validateOfferState}
            applyOfferState={applyOfferState}
            onClickPay={onClickPaySecure}
            amount={addTwoDecimalZeroes(String(getPayableAmount()))}
            convFeeComp={convenienceFeeComp()}
          />
        </Box>

        <ConvenienceFeeModal
          showModal={showModal}
          onDismiss={() => setShowModal(false)}
          convenienceFee={convenienceFee}
          transactionAmount={getTransactionAmount()}
          amount={addTwoDecimalZeroes(String(getPayableAmount()))}
        />

        <Box testID="footerContainer" {...footerStyle}>
          <PayBtn
            disabled={selectedItem.length === 0}
            selectedItem={selectedItem}
            amount={addTwoDecimalZeroes(String(getPayableAmount()))}
            onClickPaySecure={onClickPaySecure}
            isLoading={validateOfferState === 'pending' || applyOfferState === 'pending'}
          />
          {btnFooter && btnFooter}
        </Box>
      </ParentContainer>
      {showPaymentProcessModal && (
        <MobileProcessing setShow={setShowPaymentProcessModal} show={showPaymentProcessModal} />
      )}
    </Box>
  )
}

const styles = (theme: any, THEME_PADDING: any) => ({
  flatListContainer: {
    paddingHorizontal: THEME_PADDING.HORIZONTAL_PADDING,
    paddingVertical: THEME_PADDING.VERTICAL_PADDING,
    f: 1,
  } as BoxProps,
  flatListToneContainer: {
    f: 1,
  } as BoxProps,
  footerContainer: {
    paddingHorizontal: THEME_PADDING.HORIZONTAL_PADDING,
    paddingBottom: THEME_PADDING.VERTICAL_PADDING,
  } as BoxProps,
  tOneFooterContainer: {
    paddingTop: THEME_PADDING.VERTICAL_PADDING,
  } as BoxProps,
  itemText: {
    color: 'BLACK_75',
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
    fontFamily: 'jioType-medium',
  } as TextProps,
  logoContainer: {
    height: 32,
    width: 32,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  } as BoxProps,
  separatorStyle: {
    height: 1,
    backgroundColor: convertHexToRGBA(theme?.brandColor, 10) as ColorOptions,
  } as BoxProps,
  convenienceFeeText: {
    fontSize: 12,
    color: convertHexToRGBA(theme.bodyTextColor, 50) as ColorOptions,
    fontWeight: '700',
  } as TextProps,
  offerAvailable: {
    fontSize: 12,
    color: convertHexToRGBA(theme.bodyTextColor, 50) as ColorOptions,
    fontWeight: '700',
  } as TextProps,
  offerNotAvailable: {
    fontSize: 12,
    color: theme.brandColor as ColorOptions,
    fontWeight: '700',
  } as TextProps,
})

export { SectionList }
